import React, { useEffect, useState } from "react";
import axios from "axios";

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return "-";
  if (n < 1024) return `${n} B`;
  const kb = n / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

function formatDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

export default function AdminLogs() {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [dir, setDir] = useState("");
  const [error, setError] = useState("");
  const [keepDays, setKeepDays] = useState(30);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("palantir_token");
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/logs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFiles(Array.isArray(res.data?.files) ? res.data.files : []);
      setDir(String(res.data?.dir || ""));
    } catch (e) {
      console.error("[AdminLogs][load] error:", e);
      setError("Failed to load logs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleDelete(name) {
    if (!name) return;
    const ok = window.confirm(`Delete log file "${name}"?`);
    if (!ok) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("palantir_token");
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/admin/logs/${encodeURIComponent(name)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch (e) {
      console.error("[AdminLogs][delete] error:", e);
      setError("Failed to delete log.");
      setLoading(false);
    }
  }

  async function handleCleanup() {
    const n = Number(keepDays);
    if (!Number.isFinite(n) || n < 1) {
      setError("Keep days must be >= 1.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("palantir_token");
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/logs/cleanup`,
        { keep_days: n },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } catch (e) {
      console.error("[AdminLogs][cleanup] error:", e);
      setError("Failed to cleanup logs.");
      setLoading(false);
    }
  }

  async function handleDownload(name) {
    if (!name) return;
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("palantir_token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/logs/${encodeURIComponent(name)}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        }
      );

      const blob = new Blob([res.data], { type: "text/plain;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[AdminLogs][download] error:", e);
      setError("Failed to download log.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="adminCard">
      <div className="adminCardHeader">
        <h2 className="adminCardTitle">Logs</h2>
        <div className="adminCardSubtitle">
          Backend log files (daily TXT). {dir ? `Directory: ${dir}` : ""}
        </div>
      </div>

      <div className="adminCardInner">
        {error ? (
          <div className="alert alert-danger" style={{ marginBottom: 12 }}>
            {error}
          </div>
        ) : null}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <button className="btn btn-sm btn-outline-light" type="button" onClick={load} disabled={loading}>
            Refresh
          </button>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ opacity: 0.8 }}>Keep days:</span>
            <input
              type="number"
              className="form-control form-control-sm"
              style={{ width: 110 }}
              value={keepDays}
              min={1}
              onChange={(e) => setKeepDays(e.target.value)}
              disabled={loading}
            />
            <button className="btn btn-sm btn-outline-warning" type="button" onClick={handleCleanup} disabled={loading}>
              Cleanup old logs
            </button>
          </div>
        </div>

        <div
          className="table-responsive"
          style={{
            maxHeight: "500px",
            overflowY: "auto",
            overflowX: "auto",
          }}
        >
          <table
            className="table table-dark table-striped table-sm align-middle mb-0"
            style={{
              fontSize: "0.78rem",
              lineHeight: "1.0rem",
              tableLayout: "auto",
              width: "auto",
              minWidth: "900px",
              whiteSpace: "nowrap",
            }}
          >
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ textAlign: "right" }}>Size</th>
                <th>Last modified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.length ? (
                files.map((f) => (
                  <tr key={f.name}>
                    <td>{f.name}</td>
                    <td style={{ textAlign: "right" }}>{formatBytes(f.size)}</td>
                    <td>{formatDate(f.mtime)}</td>
                    <td style={{ display: "flex", gap: 8 }}>
                      <button
                        className="btn btn-sm btn-outline-info"
                        type="button"
                        onClick={() => handleDownload(f.name)}
                        disabled={loading}
                      >
                        Download
                      </button>

                      <button
                        className="btn btn-sm btn-outline-danger"
                        type="button"
                        onClick={() => handleDelete(f.name)}
                        disabled={loading || f.name === "backend-latest.txt"}
                        title={f.name === "backend-latest.txt" ? "Cannot delete latest symlink" : "Delete file"}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} style={{ opacity: 0.75 }}>
                    {loading ? "Loading..." : "No log files found."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 10, opacity: 0.7, fontSize: "0.85rem" }}>
          Tip: Use “backend-latest.txt” for quick access to the current file.
        </div>
      </div>
    </div>
  );
}
